/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React from 'react';
import descriptors from '../../../locales/en-US/descriptors';
import './UtilityTrendChart.scss';
import '../../../scss/TooltipCommonTheme.scss';
import {
  formatCurrency,
  getSymbolFromCurrency,
  partialDateCheck
} from '../Utils/Utils';
import { ILegendItem } from '@hcecbp/legend-component';
import { UtilityType } from '../Utils/Constants';
import { TFunction } from '@scuf/localization/dist/types';
import { Chart } from '@scuf/charts';
import { IConsumpData } from './ITrendChartProps';

export const actualConsumptionDataValue = (consumpData: IConsumpData) => {
  const {
    actualConsumptionData,
    category,
    actualL,
    isCompletedData,
    partialDate,
    t,
    measurementUnit,
    actualValue
  } = consumpData;
  const d = descriptors.energyManagement;
  if (actualConsumptionData) {
    return (
      <div className="tooltip-pane-item">
        <div className="tooltip-heading-container">
          <span
            className="dot"
            style={{ backgroundColor: `${actualL.color}` }}
          ></span>

          {category && category === UtilityType.CO2 ? (
            <span className="tooltip-header-label emaasThemeColor">
              {t(d.CO2)} {t(d.ACTUAL)}
              {partialDateCheck(isCompletedData, partialDate, t)}
            </span>
          ) : (
            <span className="tooltip-header-label emaasThemeColor">
              {measurementUnit}
              {t(d.ACTUAL)}
              {partialDateCheck(isCompletedData, partialDate, t)}
            </span>
          )}
        </div>
        <div>
          <span className="tooltip-header-value emaasThemeColor">
            {actualValue} {measurementUnit}
          </span>
        </div>
      </div>
    );
  } else {
    return undefined;
  }
};

export const baselineConsumptionDataValue = (
  baselineConsumptionData: (number | null)[] | undefined,
  baselineL: ILegendItem,
  measurementUnit: string,
  baselineValue: number | null | undefined,
  t: TFunction
) => {
  const d = descriptors.energyManagement;
  if (baselineConsumptionData) {
    return (
      <div className="tooltip-pane-item">
        <div className="tooltip-heading-container">
          <span
            className="dot"
            style={{ backgroundColor: `${baselineL.color}` }}
          ></span>
          <span className="tooltip-header-label emaasThemeColor">
            {measurementUnit}
            {t(d.PREVIOUSPERIOD)}
          </span>
        </div>
        <div>
          <span className="tooltip-header-value emaasThemeColor">
            {baselineValue} {measurementUnit}
          </span>
        </div>
      </div>
    );
  } else {
    return undefined;
  }
};

export const actualCostDataValue = (
  actualCostL: ILegendItem,
  costCurrency: string | undefined,
  actualCost: number | undefined,
  t: TFunction
) => {
  const d = descriptors.energyManagement;
  if (actualCostL) {
    return (
      <div className="tooltip-pane-item">
        <div className="tooltip-heading-container">
          <span
            className="dot"
            style={{ backgroundColor: `${actualCostL.color}` }}
          ></span>
          <span className="tooltip-header-label emaasThemeColor">
            {getSymbolFromCurrency(costCurrency!)}
            {t(d.ACTUAL)}
          </span>
        </div>
        <div>
          <span className="tooltip-header-value emaasThemeColor">
            {formatCurrency(actualCost!, costCurrency, false)}
          </span>
        </div>
      </div>
    );
  } else {
    return undefined;
  }
};

export const baselineCostDataValue = (
  baselineCostData: number[] | undefined,
  baselineCostL: ILegendItem,
  costCurrency: string | undefined,
  baselineCost: number | undefined,
  t: TFunction
) => {
  const d = descriptors.energyManagement;
  if (baselineCostData) {
    return (
      <div className="tooltip-pane-item">
        <div className="tooltip-heading-container">
          <span
            className="dot"
            style={{ backgroundColor: `${baselineCostL.color}` }}
          ></span>
          <span className="tooltip-header-label emaasThemeColor">
            {getSymbolFromCurrency(costCurrency!)}
            {t(d.PREVIOUSCOST)}
          </span>
        </div>
        <div>
          <span className="tooltip-header-value emaasThemeColor">
            {formatCurrency(baselineCost!, costCurrency, false)}
          </span>
        </div>
      </div>
    );
  } else {
    return undefined;
  }
};

export const chartactualCostData = (
  actualCostData: number[] | undefined,
  currencySymbol: string,
  baselineConsumptionData: (number | null)[] | undefined,
  actualConsumptionData: (number | null)[] | undefined,
  t: TFunction
) => {
  const d = descriptors.energyManagement;
  if (actualCostData) {
    return (
      <Chart.Line
        name={`${currencySymbol} ${d.ACTUALCOST.defaultMessage}`}
        data={actualCostData}
        index={undefined}
        id={'3'}
        yAxis={baselineConsumptionData || actualConsumptionData ? 1 : 0}
      />
    );
  } else {
    return undefined;
  }
};
